import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Avatar, AvatarFallback } from '../../components/ui/avatar';
import { Plus, Search, BookOpen, Users, Calendar, ChevronRight, MoreVertical, UserMinus } from 'lucide-react';
import { getBatchTypeLabel, getCourseTypeLabel, formatDate, formatTime, getInitials } from '../../lib/utils';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';

export default function BatchList() {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('all');
  const [total, setTotal] = useState(0);
  const navigate = useNavigate();

  const fetchBatches = async () => {
    setLoading(true);
    try {
      if (tab === 'recorded') {
        // Fetch recorded courses instead
        const res = await api.get('/api/courses');
        setBatches((res.data.courses || []).map(c => ({
          id: c.id, name: c.title, batch_type: 'recorded', course_type: c.course_type,
          instructor_name: '', learner_count: 0, total_videos: c.total_videos,
          next_session: null, access_type: c.access_type,
        })));
        setTotal(res.data.courses?.length || 0);
      } else {
        const params = { search, limit: 50 };
        if (tab === '1on1') params.batch_type = 'one_on_one';
        if (tab === 'group') params.batch_type = 'group';
        const res = await api.get('/api/batches', { params });
        setBatches(res.data.batches);
        setTotal(res.data.total);
      }
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  // V61.2 — debounce the search box: every keystroke used to fire a full
  // /api/batches query, which queues up behind itself on production.
  useEffect(() => {
    const t = setTimeout(fetchBatches, search ? 400 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, search]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-semibold tracking-tight">Batches</h1>
          <p className="text-muted-foreground mt-1">{total} total batches</p>
        </div>
        <div className="flex items-center gap-2">
          {tab !== 'recorded' && (
            <Button variant="outline" size="sm"
                    onClick={() => navigate('/admin/batches/archived')}
                    data-testid="view-archived-btn">
              View archived
            </Button>
          )}
          <Button onClick={() => navigate(tab === 'recorded' ? '/admin/courses' : '/admin/batches/create')} data-testid="create-batch-button">
            <Plus className="h-4 w-4 mr-2" />{tab === 'recorded' ? 'Create Recorded Course' : 'Create Batch'}
          </Button>
        </div>
      </div>

      <Card className="rounded-2xl">
        <CardContent className="pt-5">
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <Tabs value={tab} onValueChange={setTab} className="flex-1">
              <TabsList>
                <TabsTrigger value="all">All</TabsTrigger>
                <TabsTrigger value="1on1">1:1</TabsTrigger>
                <TabsTrigger value="group">Group</TabsTrigger>
                <TabsTrigger value="recorded">Recorded</TabsTrigger>
              </TabsList>
            </Tabs>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search batches..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 w-full sm:w-64" data-testid="batch-search-input" />
            </div>
          </div>

          {loading ? (
            <div className="space-y-3">{[...Array(5)].map((_, i) => <div key={i} className="h-16 bg-muted/40 rounded-xl animate-pulse" />)}</div>
          ) : batches.length === 0 ? (
            <div className="text-center py-12">
              <BookOpen className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">No batches found</p>
              <Button variant="outline" className="mt-4" onClick={() => navigate('/admin/batches/create')}>Create your first batch</Button>
            </div>
          ) : (
            <Table data-testid="batch-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Batch Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Course</TableHead>
                  <TableHead>Instructor</TableHead>
                  <TableHead>Learners</TableHead>
                  <TableHead>Next Session</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {batches.map(batch => (
                  <TableRow key={batch.id} className="cursor-pointer hover:bg-muted/40" onClick={() => batch.batch_type === 'recorded' ? navigate(`/admin/courses/${batch.id}`) : navigate(`/admin/batches/${batch.id}`)}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        {batch.name}
                        {batch.next_session?.status === 'live' && <Badge className="bg-rose-500 text-white border-0 text-[9px] animate-pulse">Live</Badge>}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={batch.batch_type === 'one_on_one' ? 'bg-teal-50 text-teal-800 border-teal-200' : batch.batch_type === 'recorded' ? 'bg-violet-50 text-violet-800 border-violet-200' : 'bg-orange-50 text-orange-800 border-orange-200'}>
                        {batch.batch_type === 'recorded' ? 'Recorded' : getBatchTypeLabel(batch.batch_type)}
                      </Badge>
                    </TableCell>
                    <TableCell><Badge variant="secondary">{getCourseTypeLabel(batch.course_type)}</Badge></TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Avatar className="h-6 w-6"><AvatarFallback className="text-[10px] bg-primary/10 text-primary">{getInitials(batch.instructor_name)}</AvatarFallback></Avatar>
                        <span className="text-sm">{batch.instructor_name || '-'}</span>
                      </div>
                    </TableCell>
                    <TableCell>{batch.batch_type === 'one_on_one' ? (batch.learner_name || '-') : `${batch.learner_count} learners`}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {batch.next_session ? `${formatDate(batch.next_session.scheduled_date)} ${formatTime(batch.next_session.scheduled_time)}` : 'No sessions'}
                    </TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`batch-actions-${batch.id}`}>
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/admin/batches/${batch.id}`)}>
                            Open batch
                          </DropdownMenuItem>
                          {batch.instructor_name && batch.batch_type !== 'recorded' && (
                            <DropdownMenuItem
                              className="text-destructive"
                              data-testid={`remove-instructor-${batch.id}`}
                              onClick={async () => {
                                if (!window.confirm(`Remove ${batch.instructor_name} as instructor from "${batch.name}"? All upcoming sessions will be unassigned.`)) return;
                                try {
                                  const res = await api.delete(`/api/batches/${batch.id}/instructor`);
                                  toast.success(res.data.message + (res.data.cascaded_sessions ? ` (${res.data.cascaded_sessions} upcoming sessions cleared)` : ''));
                                  fetchBatches();
                                } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
                              }}
                            >
                              <UserMinus className="h-4 w-4 mr-2" />Remove instructor
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
